import { useEffect, useMemo, useRef, useState } from 'react'
import {
  allowedBetCardTreatmentFormats,
  betCardVisibleLegOptions,
  defaultBetCardTreatmentFormat,
  DEFAULT_BET_CARD_TREATMENT,
  normalizeBetCardTreatmentConfig,
} from '@feed-widget/surfaces/betCardTreatment'
import { Button, Field, Input, Select, Switch } from '../common'
import styles from './BetCardTreatmentControls.module.css'

const FORMAT_OPTIONS = [
  {
    value: 'compact_carousel',
    label: 'Compact sportsbook tile',
    description: 'A dense, price-first tile with two or three visible legs.',
  },
  {
    value: 'hub_stack',
    label: 'Expanded bet card',
    description: 'A vertical card with four to six visible legs and full price details.',
  },
  {
    value: 'feed_story',
    label: 'Editorial bet story',
    description: 'A creator-led story with optional media and two or three visible legs.',
  },
]

function treatmentDraft(config, displayType) {
  const normalized = normalizeBetCardTreatmentConfig(config)
  const hasPersistedTreatment = Boolean(
    config?.bet_card_treatment && typeof config.bet_card_treatment === 'object',
  )
  const supportedFormats = allowedBetCardTreatmentFormats(displayType)
  const fallbackFormat = defaultBetCardTreatmentFormat(displayType)
  const format = normalized && supportedFormats.includes(normalized.format)
    ? normalized.format
    : fallbackFormat || normalized?.format || DEFAULT_BET_CARD_TREATMENT.format
  const visibleLegOptions = betCardVisibleLegOptions(format)
  const requestedVisibleLegs = normalized?.visible_legs ?? DEFAULT_BET_CARD_TREATMENT.visible_legs
  return {
    enabled: hasPersistedTreatment,
    needsRepair: hasPersistedTreatment && (
      !normalized
      || config.bet_card_treatment.format !== normalized.format
      || !supportedFormats.includes(normalized.format)
    ),
    treatment: {
      ...(normalized || DEFAULT_BET_CARD_TREATMENT),
      format,
      visible_legs: visibleLegOptions.includes(requestedVisibleLegs)
        ? requestedVisibleLegs
        : visibleLegOptions[Math.floor(visibleLegOptions.length / 2)],
    },
  }
}

function withoutKey(source, key) {
  const next = { ...(source || {}) }
  delete next[key]
  return next
}

function sameEditableDraft(left, right) {
  return left.enabled === right.enabled
    && JSON.stringify(left.treatment) === JSON.stringify(right.treatment)
}

export default function BetCardTreatmentControls({ surface, onSave }) {
  const sourceKey = `${surface?.id || ''}:${surface?.display_type || ''}`
  const sourceConfig = surface?.config
  const displayType = surface?.display_type
  const supportedFormats = allowedBetCardTreatmentFormats(displayType)
  const treatmentSupported = supportedFormats.length > 0
  const persisted = useMemo(
    () => treatmentDraft(sourceConfig, displayType),
    [displayType, sourceConfig],
  )
  const [draft, setDraft] = useState(() => treatmentDraft(sourceConfig, displayType))
  const previousPersisted = useRef({ sourceKey, value: persisted })

  useEffect(() => {
    const previous = previousPersisted.current
    previousPersisted.current = { sourceKey, value: persisted }
    setDraft((current) => {
      const changedSurface = previous.sourceKey !== sourceKey
      const hadUnsavedChanges = previous.value.needsRepair
        || !sameEditableDraft(current, previous.value)
      const saveLanded = !persisted.needsRepair && sameEditableDraft(current, persisted)
      return changedSurface || !hadUnsavedChanges || saveLanded ? persisted : current
    })
  }, [persisted, sourceKey])

  const dirty = persisted.needsRepair || JSON.stringify(draft) !== JSON.stringify(persisted)
  const updateTreatment = (field, value) => setDraft((current) => ({
    ...current,
    treatment: { ...current.treatment, [field]: value },
  }))
  const updateFormat = (format) => {
    const options = betCardVisibleLegOptions(format)
    setDraft((current) => ({
      ...current,
      treatment: {
        ...current.treatment,
        format,
        visible_legs: options.includes(current.treatment.visible_legs)
          ? current.treatment.visible_legs
          : options[Math.floor(options.length / 2)],
      },
    }))
  }
  const visibleLegOptions = betCardVisibleLegOptions(draft.treatment.format)
  const selectedFormatOptions = FORMAT_OPTIONS.filter((option) => (
    supportedFormats.includes(option.value)
  ))

  const save = () => {
    const currentConfig = sourceConfig || {}
    if (!draft.enabled) {
      onSave({ config: withoutKey(currentConfig, 'bet_card_treatment') })
      return
    }
    onSave({
      config: {
        ...currentConfig,
        bet_card_treatment: {
          ...draft.treatment,
          version: 1,
        },
      },
    })
  }

  return (
    <div className={styles.module} data-testid="bet-card-treatment-controls">
      <Switch
        label="Use a bet card treatment"
        description={treatmentSupported
          ? 'Multi-leg bets use a sportsbook-style card. Other posts keep this surface’s standard card.'
          : 'Bet card treatments require a Horizontal carousel or Story block layout.'}
        checked={draft.enabled}
        disabled={!treatmentSupported && !draft.enabled}
        onChange={(enabled) => setDraft((current) => ({ ...current, enabled }))}
      />

      {draft.enabled && !treatmentSupported ? (
        <p className={styles.compatibilityNotice}>
          Change this surface to Horizontal carousel or Story block, or turn off the treatment.
        </p>
      ) : null}

      {draft.enabled && draft.needsRepair && treatmentSupported ? (
        <p className={styles.compatibilityNotice}>
          These saved settings are no longer compatible with this layout. Review and save them.
        </p>
      ) : null}

      {draft.enabled && treatmentSupported ? (
        <>
          <Field
            label="Treatment"
            help="Choose how multi-leg bets look when they appear on this surface."
          >
            <div className={styles.formatGrid} aria-label="Bet card treatment">
              {selectedFormatOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={draft.treatment.format === option.value}
                  onClick={() => updateFormat(option.value)}
                >
                  <strong>{option.label}</strong>
                  <small>{option.description}</small>
                </button>
              ))}
            </div>
          </Field>

          <div className={styles.twoColumns}>
            <Field label="Visible legs">
              <Select
                value={draft.treatment.visible_legs}
                onChange={(event) => updateTreatment('visible_legs', Number(event.target.value))}
              >
                {visibleLegOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </Select>
            </Field>
            <Field label="Example stake">
              <Input
                type="number"
                min="1"
                max="1000"
                step="1"
                value={draft.treatment.stake_cents / 100}
                onChange={(event) => updateTreatment(
                  'stake_cents',
                  Math.round(Math.min(1000, Math.max(1, Number(event.target.value) || 1)) * 100),
                )}
              />
            </Field>
          </div>

          <div className={styles.switches}>
            <Switch
              label="Show social proof"
              description="Use the verified number of times the bet was copied."
              checked={draft.treatment.show_social_proof}
              onChange={(value) => updateTreatment('show_social_proof', value)}
            />
            <Switch
              label="Show rationale"
              description="Let readers open the post’s editorial rationale."
              checked={draft.treatment.show_rationale}
              onChange={(value) => updateTreatment('show_rationale', value)}
            />
            <Switch
              label="Show media"
              description="Use attached media as a secondary element on the bet card."
              checked={draft.treatment.show_media}
              onChange={(value) => updateTreatment('show_media', value)}
            />
            {draft.treatment.show_media ? (
              <Switch
                label="Spotlight first card only"
                description="Use media on the leading card and keep the remaining cards dense."
                checked={draft.treatment.spotlight_first}
                onChange={(value) => updateTreatment('spotlight_first', value)}
              />
            ) : null}
          </div>
        </>
      ) : null}

      <div className={styles.actions}>
        <span>{dirty ? 'Unsaved card treatment changes' : 'Card treatment is saved'}</span>
        <Button
          type="button"
          size="sm"
          disabled={!dirty || (draft.enabled && !treatmentSupported)}
          onClick={save}
        >
          Save card treatment
        </Button>
      </div>
    </div>
  )
}
